import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiBook, FiUpload, FiSearch, FiPlus, FiEdit2, FiTrash2,
  FiX, FiCheck, FiFilter, FiGrid, FiList, FiExternalLink,
  FiFileText, FiGlobe, FiBookOpen, FiAlertCircle, FiDownload,
  FiUser, FiMail, FiCalendar, FiMessageSquare
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './BukuSaya.css';

const BukuSaya = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState({ total: 0, uploaded: 0, claimed: 0, pending: 0 });
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, totalPages: 0 });
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  
  // Claim search
  const [claimSearchResults, setClaimSearchResults] = useState([]);
  const [claimSearchLoading, setClaimSearchLoading] = useState(false);
  const [claimSearchQuery, setClaimSearchQuery] = useState('');
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    judul: '',
    penulis: user?.nama || '',
    isbn: '',
    tahun_terbit: new Date().getFullYear(),
    id_kategori: '',
    deskripsi: '',
    bahasa: 'Indonesia',
    jumlah_halaman: '',
    cover: null,
    pdf: null
  });
  const [uploading, setUploading] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  
  // Edit form
  const [editForm, setEditForm] = useState({
    id_buku: '',
    judul: '',
    penulis: '',
    isbn: '',
    tahun_terbit: '',
    id_kategori: '',
    deskripsi: '',
    bahasa: 'Indonesia',
    jumlah_halaman: ''
  });
  const [editing, setEditing] = useState(false);

  // Sitasi request modal states
  const [showSitasiModal, setShowSitasiModal] = useState(false);
  const [sitasiRequests, setSitasiRequests] = useState([]);
  const [sitasiLoading, setSitasiLoading] = useState(false);
  const [sitasiStats, setSitasiStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [processingRequest, setProcessingRequest] = useState(null);
  const [sitasiNotes, setSitasiNotes] = useState({}); // Store notes per request id
  const [expandedRequest, setExpandedRequest] = useState(null); // Which request is expanded for action

  // Fetch sitasi requests
  const fetchSitasiRequests = useCallback(async () => {
    if (!user?.id) return;
    
    setSitasiLoading(true);
    try {
      const response = await api.getCitationRequests(user.id);
      if (response.success) {
        setSitasiRequests(response.data || []);
        setSitasiStats(response.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching sitasi requests:', error);
    } finally {
      setSitasiLoading(false);
    }
  }, [user?.id]);

  // Handle approve/reject sitasi request
  const handleSitasiAction = async (idRequest, status, catatanDosen = '') => {
    setProcessingRequest(idRequest);
    try {
      const response = await api.updateCitationRequest({
        id_request: idRequest,
        user_id: user?.id,
        status: status,
        catatan_dosen: catatanDosen
      });
      
      if (response.success) {
        // Reset form state
        setExpandedRequest(null);
        setSitasiNotes(prev => ({ ...prev, [idRequest]: '' }));
        // Refresh data
        fetchSitasiRequests();
        fetchMyBooks(); // Refresh stats
      }
    } catch (error) {
      console.error('Error updating sitasi request:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  // Fetch my books
  const fetchMyBooks = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await api.getMyBooks(user.id, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        filter: activeTab === 'all' ? '' : activeTab
      });
      
      if (response.success) {
        setBooks(response.data || []);
        setStats(response.stats || { total: 0, uploaded: 0, claimed: 0, pending: 0 });
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total_rows || 0,
          totalPages: response.pagination?.total_pages || 1
        }));
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, pagination.page, pagination.limit, searchQuery, activeTab]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchMyBooks();
  }, [fetchMyBooks]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Search books for claiming
  const searchForClaim = async () => {
    if (!user?.id) return;
    
    setClaimSearchLoading(true);
    try {
      const response = await api.searchBooksForClaim(user.id, user.nama, claimSearchQuery);
      if (response.success) {
        setClaimSearchResults(response.data || []);
      }
    } catch (error) {
      console.error('Error searching books:', error);
    } finally {
      setClaimSearchLoading(false);
    }
  };

  useEffect(() => {
    if (showClaimModal) {
      searchForClaim();
    }
  }, [showClaimModal]);

  // Claim a book
  const handleClaimBook = async (bookId) => {
    try {
      const response = await api.claimBook(user.id, bookId);
      if (response.success) {
        alert(response.message);
        setShowClaimModal(false);
        fetchMyBooks();
      } else {
        alert(response.message);
      }
    } catch (error) {
      alert('Gagal mengklaim buku');
    }
  };

  // Handle cover file change
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({ ...prev, cover: file }));
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  // Handle PDF file change
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({ ...prev, pdf: file }));
    }
  };

  // Upload book
  const handleUploadBook = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.judul.trim()) {
      alert('Judul buku wajib diisi');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', user.id);
      formData.append('judul', uploadForm.judul);
      formData.append('penulis', uploadForm.penulis);
      formData.append('isbn', uploadForm.isbn);
      formData.append('tahun_terbit', uploadForm.tahun_terbit);
      formData.append('id_kategori', uploadForm.id_kategori);
      formData.append('deskripsi', uploadForm.deskripsi);
      formData.append('bahasa', uploadForm.bahasa);
      formData.append('jumlah_halaman', uploadForm.jumlah_halaman);
      
      if (uploadForm.cover) {
        formData.append('cover', uploadForm.cover);
      }
      if (uploadForm.pdf) {
        formData.append('pdf', uploadForm.pdf);
      }
      
      const response = await api.uploadBook(formData);
      
      if (response.success) {
        alert('Buku berhasil diupload!');
        setShowUploadModal(false);
        resetUploadForm();
        fetchMyBooks();
      } else {
        alert(response.message || 'Gagal mengupload buku');
      }
    } catch (error) {
      alert('Terjadi kesalahan saat mengupload buku');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      judul: '',
      penulis: user?.nama || '',
      isbn: '',
      tahun_terbit: new Date().getFullYear(),
      id_kategori: '',
      deskripsi: '',
      bahasa: 'Indonesia',
      jumlah_halaman: '',
      cover: null,
      pdf: null
    });
    setCoverPreview(null);
  };

  // Open edit modal with book data
  const openEditModal = (book) => {
    setEditForm({
      id_buku: book.id_buku,
      judul: book.judul || '',
      penulis: book.penulis || '',
      isbn: book.isbn || '',
      tahun_terbit: book.tahun_terbit || '',
      id_kategori: book.id_kategori || '',
      deskripsi: book.deskripsi || '',
      bahasa: book.bahasa || 'Indonesia',
      jumlah_halaman: book.jumlah_halaman || ''
    });
    setSelectedBook(book);
    setShowEditModal(true);
  };

  // Update book
  const handleUpdateBook = async (e) => {
    e.preventDefault();
    
    if (!editForm.judul.trim()) {
      alert('Judul buku wajib diisi');
      return;
    }
    
    setEditing(true);
    try {
      const response = await api.updateMyBook({
        user_id: user.id,
        id_buku: editForm.id_buku,
        judul: editForm.judul,
        penulis: editForm.penulis,
        isbn: editForm.isbn,
        tahun_terbit: editForm.tahun_terbit,
        id_kategori: editForm.id_kategori,
        deskripsi: editForm.deskripsi,
        bahasa: editForm.bahasa,
        jumlah_halaman: editForm.jumlah_halaman
      });
      
      if (response.success) {
        alert('Buku berhasil diupdate!');
        setShowEditModal(false);
        setSelectedBook(null);
        fetchMyBooks();
      } else {
        alert(response.message || 'Gagal mengupdate buku');
      }
    } catch (error) {
      alert('Terjadi kesalahan saat mengupdate buku');
    } finally {
      setEditing(false);
    }
  };

  // Delete/Unclaim book
  const handleDeleteBook = async () => {
    if (!selectedBook) return;
    
    try {
      const response = await api.deleteMyBook(user.id, selectedBook.id_buku);
      if (response.success) {
        alert(response.message);
        setShowDeleteModal(false);
        setSelectedBook(null);
        fetchMyBooks();
      } else {
        alert(response.message);
      }
    } catch (error) {
      alert('Gagal menghapus buku');
    }
  };

  const getTabFilter = (tab) => {
    switch (tab) {
      case 'uploaded': return 'uploaded';
      case 'claimed': return 'claimed';
      case 'pending': return 'pending';
      default: return '';
    }
  };

  return (
    <div className="buku-saya">
      {/* Header */}
      <header className="buku-saya__header">
        <div className="buku-saya__header-left">
          <h1 className="buku-saya__title">
            <FiBook /> Buku Saya
          </h1>
          <p className="buku-saya__subtitle">
            Kelola buku yang Anda klaim dan upload
          </p>
        </div>
        <div className="buku-saya__header-actions">
          <button 
            className="buku-saya__btn buku-saya__btn--secondary"
            onClick={() => setShowClaimModal(true)}
          >
            <FiSearch /> Klaim Buku
          </button>
          <button 
            className="buku-saya__btn buku-saya__btn--primary"
            onClick={() => setShowUploadModal(true)}
          >
            <FiUpload /> Upload Buku
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="buku-saya__stats">
        <div className="buku-saya__stat-card" onClick={() => setActiveTab('all')}>
          <div className="buku-saya__stat-icon buku-saya__stat-icon--total">
            <FiBook />
          </div>
          <div className="buku-saya__stat-info">
            <span className="buku-saya__stat-number">{stats.total}</span>
            <span className="buku-saya__stat-label">Total Buku</span>
          </div>
        </div>
        <div className="buku-saya__stat-card" onClick={() => setActiveTab('uploaded')}>
          <div className="buku-saya__stat-icon buku-saya__stat-icon--uploaded">
            <FiUpload />
          </div>
          <div className="buku-saya__stat-info">
            <span className="buku-saya__stat-number">{stats.uploaded}</span>
            <span className="buku-saya__stat-label">Diupload</span>
          </div>
        </div>
        <div className="buku-saya__stat-card" onClick={() => setActiveTab('claimed')}>
          <div className="buku-saya__stat-icon buku-saya__stat-icon--claimed">
            <FiCheck />
          </div>
          <div className="buku-saya__stat-info">
            <span className="buku-saya__stat-number">{stats.claimed}</span>
            <span className="buku-saya__stat-label">Diklaim</span>
          </div>
        </div>
        <div className="buku-saya__stat-card" onClick={() => setActiveTab('pending')}>
          <div className="buku-saya__stat-icon buku-saya__stat-icon--pending">
            <FiAlertCircle />
          </div>
          <div className="buku-saya__stat-info">
            <span className="buku-saya__stat-number">{stats.pending}</span>
            <span className="buku-saya__stat-label">Pending</span>
          </div>
        </div>
        {stats.pending_sitasi > 0 && (
          <div 
            className="buku-saya__stat-card buku-saya__stat-card--sitasi"
            onClick={() => {
              setShowSitasiModal(true);
              fetchSitasiRequests();
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="buku-saya__stat-icon buku-saya__stat-icon--sitasi">
              <FiFileText />
            </div>
            <div className="buku-saya__stat-info">
              <span className="buku-saya__stat-number">{stats.pending_sitasi}</span>
              <span className="buku-saya__stat-label">Request Sitasi</span>
            </div>
            <span className="buku-saya__stat-badge-new">Baru</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="buku-saya__filters">
        <div className="buku-saya__tabs">
          {['all', 'uploaded', 'claimed', 'pending'].map(tab => (
            <button 
              key={tab}
              className={`buku-saya__tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              {tab === 'all' ? 'Semua' : 
               tab === 'uploaded' ? 'Diupload' : 
               tab === 'claimed' ? 'Diklaim' : 'Pending'}
            </button>
          ))}
        </div>

        <div className="buku-saya__filter-actions">
          <div className="buku-saya__search">
            <FiSearch />
            <input 
              type="text"
              placeholder="Cari buku..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="buku-saya__view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
          </div>
        </div>
      </div>

      {/* Books Grid/List */}
      {loading ? (
        <div className="buku-saya__loading">
          <div className="buku-saya__loading-spinner"></div>
          <p>Memuat data...</p>
        </div>
      ) : books.length === 0 ? (
        <div className="buku-saya__empty">
          <FiBook className="buku-saya__empty-icon" />
          <h3>Belum ada buku</h3>
          <p>Mulai dengan mengupload buku baru atau klaim buku yang sudah ada</p>
          <div className="buku-saya__empty-actions">
            <button 
              className="buku-saya__btn buku-saya__btn--secondary"
              onClick={() => setShowClaimModal(true)}
            >
              <FiSearch /> Klaim Buku
            </button>
            <button 
              className="buku-saya__btn buku-saya__btn--primary"
              onClick={() => setShowUploadModal(true)}
            >
              <FiUpload /> Upload Buku
            </button>
          </div>
        </div>
      ) : (
        <div className={`buku-saya__books buku-saya__books--${viewMode}`}>
          {books.map(book => (
            <div key={book.id_buku} className="buku-saya__book">
              <div className="buku-saya__book-cover">
                <img 
                  src={book.cover_image || '/images/cover-default.png'} 
                  alt={book.judul}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/cover-default.png';
                  }}
                />
                <span className={`buku-saya__book-badge buku-saya__book-badge--${book.sumber_buku === 'Upload Dosen' ? 'uploaded' : 'claimed'}`}>
                  {book.sumber_buku === 'Upload Dosen' ? 'Diupload' : 'Diklaim'}
                </span>
                {/* Badge untuk pending request sitasi */}
                {parseInt(book.pending_sitasi) > 0 && (
                  <span className="buku-saya__book-sitasi-badge" title={`${book.pending_sitasi} Request Sitasi Menunggu`}>
                    <FiFileText />
                    {book.pending_sitasi}
                  </span>
                )}
              </div>
              <div className="buku-saya__book-info">
                <h3 className="buku-saya__book-title">{book.judul}</h3>
                <p className="buku-saya__book-author">{book.penulis || 'Unknown'}</p>
                <div className="buku-saya__book-meta">
                  <span className="buku-saya__book-category">{book.nama_kategori || 'Umum'}</span>
                  {book.tahun_terbit && (
                    <span className="buku-saya__book-year">{book.tahun_terbit}</span>
                  )}
                </div>
                {book.tipe_buku === 'digital' && book.link_external && (
                  <a 
                    href={book.link_external.startsWith('http') ? book.link_external : `${book.link_external}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buku-saya__book-download"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FiDownload /> Download PDF
                  </a>
                )}
              </div>
              <div className="buku-saya__book-actions">
                <button 
                  className="buku-saya__book-action buku-saya__book-action--edit"
                  onClick={() => openEditModal(book)}
                  title="Edit"
                >
                  <FiEdit2 />
                </button>
                <button 
                  className="buku-saya__book-action buku-saya__book-action--delete"
                  onClick={() => {
                    setSelectedBook(book);
                    setShowDeleteModal(true);
                  }}
                  title={book.sumber_buku === 'Upload Dosen' ? 'Hapus' : 'Batalkan Klaim'}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="buku-saya__pagination">
          <button 
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
          >
            Sebelumnya
          </button>
          <span>Halaman {pagination.page} dari {pagination.totalPages}</span>
          <button 
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.totalPages}
          >
            Selanjutnya
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="buku-saya__modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="buku-saya__modal buku-saya__modal--large" onClick={e => e.stopPropagation()}>
            <div className="buku-saya__modal-header">
              <h2><FiUpload /> Upload Buku Baru</h2>
              <button onClick={() => setShowUploadModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleUploadBook} className="buku-saya__modal-body">
              <div className="buku-saya__form-grid">
                <div className="buku-saya__form-left">
                  {/* Cover Preview */}
                  <div className="buku-saya__cover-upload">
                    <div className="buku-saya__cover-preview">
                      {coverPreview ? (
                        <img src={coverPreview} alt="Cover Preview" />
                      ) : (
                        <div className="buku-saya__cover-placeholder">
                          <FiBook />
                          <span>Cover Buku</span>
                        </div>
                      )}
                    </div>
                    <label className="buku-saya__cover-btn">
                      <FiUpload /> Pilih Cover
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleCoverChange}
                        hidden 
                      />
                    </label>
                  </div>

                  {/* PDF Upload */}
                  <div className="buku-saya__pdf-upload">
                    <label className="buku-saya__pdf-btn">
                      <FiFileText />
                      {uploadForm.pdf ? uploadForm.pdf.name : 'Upload PDF (Opsional)'}
                      <input 
                        type="file" 
                        accept=".pdf" 
                        onChange={handlePdfChange}
                        hidden 
                      />
                    </label>
                    <p className="buku-saya__pdf-info">
                      Upload file PDF jika buku berformat digital
                    </p>
                  </div>
                </div>

                <div className="buku-saya__form-right">
                  <div className="buku-saya__form-group">
                    <label>Judul Buku *</label>
                    <input 
                      type="text"
                      value={uploadForm.judul}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, judul: e.target.value }))}
                      placeholder="Masukkan judul buku"
                      required
                    />
                  </div>

                  <div className="buku-saya__form-group">
                    <label>Penulis</label>
                    <input 
                      type="text"
                      value={uploadForm.penulis}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, penulis: e.target.value }))}
                      placeholder="Nama penulis"
                    />
                  </div>

                  <div className="buku-saya__form-row">
                    <div className="buku-saya__form-group">
                      <label>ISBN</label>
                      <input 
                        type="text"
                        value={uploadForm.isbn}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, isbn: e.target.value }))}
                        placeholder="ISBN"
                      />
                    </div>
                    <div className="buku-saya__form-group">
                      <label>Tahun Terbit</label>
                      <input 
                        type="number"
                        value={uploadForm.tahun_terbit}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, tahun_terbit: e.target.value }))}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                      />
                    </div>
                  </div>

                  <div className="buku-saya__form-row">
                    <div className="buku-saya__form-group">
                      <label>Kategori</label>
                      <select 
                        value={uploadForm.id_kategori}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, id_kategori: e.target.value }))}
                      >
                        <option value="">Pilih Kategori</option>
                        {categories.map(cat => (
                          <option key={cat.id_kategori} value={cat.id_kategori}>
                            {cat.nama_kategori}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="buku-saya__form-group">
                      <label>Jumlah Halaman</label>
                      <input 
                        type="number"
                        value={uploadForm.jumlah_halaman}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, jumlah_halaman: e.target.value }))}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="buku-saya__form-group">
                    <label>Deskripsi</label>
                    <textarea 
                      value={uploadForm.deskripsi}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, deskripsi: e.target.value }))}
                      placeholder="Deskripsi singkat tentang buku"
                      rows="4"
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="buku-saya__modal-footer">
                <button 
                  type="button" 
                  className="buku-saya__btn buku-saya__btn--secondary"
                  onClick={() => setShowUploadModal(false)}
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  className="buku-saya__btn buku-saya__btn--primary"
                  disabled={uploading}
                >
                  {uploading ? 'Mengupload...' : 'Upload Buku'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Claim Modal */}
      {showClaimModal && (
        <div className="buku-saya__modal-overlay" onClick={() => setShowClaimModal(false)}>
          <div className="buku-saya__modal buku-saya__modal--large" onClick={e => e.stopPropagation()}>
            <div className="buku-saya__modal-header">
              <h2><FiSearch /> Klaim Buku</h2>
              <button onClick={() => setShowClaimModal(false)}><FiX /></button>
            </div>
            <div className="buku-saya__modal-body">
              <p className="buku-saya__claim-info">
                Cari buku yang Anda tulis dari database perpustakaan untuk diklaim sebagai milik Anda
              </p>
              
              <div className="buku-saya__claim-search">
                <FiSearch />
                <input 
                  type="text"
                  placeholder="Cari berdasarkan judul atau ISBN..."
                  value={claimSearchQuery}
                  onChange={(e) => setClaimSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchForClaim()}
                />
                <button onClick={searchForClaim}>Cari</button>
              </div>

              {claimSearchLoading ? (
                <div className="buku-saya__claim-loading">
                  <div className="buku-saya__loading-spinner"></div>
                  <p>Mencari buku...</p>
                </div>
              ) : claimSearchResults.length === 0 ? (
                <div className="buku-saya__claim-empty">
                  <FiBook />
                  <p>Tidak ada buku yang ditemukan</p>
                </div>
              ) : (
                <div className="buku-saya__claim-results">
                  {claimSearchResults.map(book => (
                    <div key={book.id_buku} className="buku-saya__claim-item">
                      <div className="buku-saya__claim-item-cover">
                        <img 
                          src={book.cover_image || '/images/cover-default.png'} 
                          alt={book.judul}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/cover-default.png';
                          }}
                        />
                      </div>
                      <div className="buku-saya__claim-item-info">
                        <h4>{book.judul}</h4>
                        <p className="buku-saya__claim-item-author">{book.penulis}</p>
                        <div className="buku-saya__claim-item-meta">
                          {book.tahun_terbit && <span>{book.tahun_terbit}</span>}
                          {book.nama_kategori && <span>{book.nama_kategori}</span>}
                          {book.sumber_buku && <span>{book.sumber_buku}</span>}
                        </div>
                      </div>
                      <button 
                        className="buku-saya__claim-btn"
                        onClick={() => handleClaimBook(book.id_buku)}
                      >
                        <FiCheck /> Klaim
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {showEditModal && selectedBook && (
        <div className="buku-saya__modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="buku-saya__modal buku-saya__modal--large" onClick={e => e.stopPropagation()}>
            <div className="buku-saya__modal-header">
              <h2><FiEdit2 /> Edit Buku</h2>
              <button onClick={() => setShowEditModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleUpdateBook} className="buku-saya__modal-body">
              <div className="buku-saya__form-right" style={{ maxWidth: '100%' }}>
                <div className="buku-saya__form-group">
                  <label>Judul Buku *</label>
                  <input 
                    type="text"
                    value={editForm.judul}
                    onChange={(e) => setEditForm(prev => ({ ...prev, judul: e.target.value }))}
                    placeholder="Masukkan judul buku"
                    required
                  />
                </div>

                <div className="buku-saya__form-group">
                  <label>Penulis</label>
                  <input 
                    type="text"
                    value={editForm.penulis}
                    onChange={(e) => setEditForm(prev => ({ ...prev, penulis: e.target.value }))}
                    placeholder="Nama penulis"
                  />
                </div>

                <div className="buku-saya__form-row">
                  <div className="buku-saya__form-group">
                    <label>ISBN</label>
                    <input 
                      type="text"
                      value={editForm.isbn}
                      onChange={(e) => setEditForm(prev => ({ ...prev, isbn: e.target.value }))}
                      placeholder="ISBN"
                    />
                  </div>
                  <div className="buku-saya__form-group">
                    <label>Tahun Terbit</label>
                    <input 
                      type="number"
                      value={editForm.tahun_terbit}
                      onChange={(e) => setEditForm(prev => ({ ...prev, tahun_terbit: e.target.value }))}
                      min="1900"
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                </div>

                <div className="buku-saya__form-row">
                  <div className="buku-saya__form-group">
                    <label>Kategori</label>
                    <select 
                      value={editForm.id_kategori}
                      onChange={(e) => setEditForm(prev => ({ ...prev, id_kategori: e.target.value }))}
                    >
                      <option value="">Pilih Kategori</option>
                      {categories.map(cat => (
                        <option key={cat.id_kategori} value={cat.id_kategori}>
                          {cat.nama_kategori}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="buku-saya__form-group">
                    <label>Jumlah Halaman</label>
                    <input 
                      type="number"
                      value={editForm.jumlah_halaman}
                      onChange={(e) => setEditForm(prev => ({ ...prev, jumlah_halaman: e.target.value }))}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="buku-saya__form-group">
                  <label>Bahasa</label>
                  <select 
                    value={editForm.bahasa}
                    onChange={(e) => setEditForm(prev => ({ ...prev, bahasa: e.target.value }))}
                  >
                    <option value="Indonesia">Indonesia</option>
                    <option value="Inggris">Inggris</option>
                    <option value="Lainnya">Lainnya</option>
                  </select>
                </div>

                <div className="buku-saya__form-group">
                  <label>Deskripsi</label>
                  <textarea 
                    value={editForm.deskripsi}
                    onChange={(e) => setEditForm(prev => ({ ...prev, deskripsi: e.target.value }))}
                    placeholder="Deskripsi singkat tentang buku"
                    rows="4"
                  ></textarea>
                </div>
              </div>

              <div className="buku-saya__modal-footer">
                <button 
                  type="button" 
                  className="buku-saya__btn buku-saya__btn--secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  className="buku-saya__btn buku-saya__btn--primary"
                  disabled={editing}
                >
                  {editing ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedBook && (
        <div className="buku-saya__modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="buku-saya__modal buku-saya__modal--small" onClick={e => e.stopPropagation()}>
            <div className="buku-saya__modal-header">
              <h2><FiAlertCircle /> Konfirmasi</h2>
              <button onClick={() => setShowDeleteModal(false)}><FiX /></button>
            </div>
            <div className="buku-saya__modal-body">
              <p>
                {selectedBook.sumber_buku === 'Upload Dosen' 
                  ? `Apakah Anda yakin ingin menghapus buku "${selectedBook.judul}"? Tindakan ini tidak dapat dibatalkan.`
                  : `Apakah Anda yakin ingin membatalkan klaim buku "${selectedBook.judul}"?`
                }
              </p>
            </div>
            <div className="buku-saya__modal-footer">
              <button 
                className="buku-saya__btn buku-saya__btn--secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Batal
              </button>
              <button 
                className="buku-saya__btn buku-saya__btn--danger"
                onClick={handleDeleteBook}
              >
                {selectedBook.sumber_buku === 'Upload Dosen' ? 'Hapus' : 'Batalkan Klaim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sitasi Request Modal */}
      {showSitasiModal && (
        <div className="buku-saya__modal-overlay" onClick={() => setShowSitasiModal(false)}>
          <div className="buku-saya__modal buku-saya__modal--large sitasi-modal" onClick={e => e.stopPropagation()}>
            <div className="buku-saya__modal-header sitasi-header">
              <h2><FiFileText /> Request Sitasi dari Mahasiswa</h2>
              <button onClick={() => setShowSitasiModal(false)}><FiX /></button>
            </div>
            
            {/* Sitasi Stats */}
            <div className="sitasi-stats">
              <div className="sitasi-stat sitasi-stat--pending">
                <span className="sitasi-stat__number">{sitasiStats.pending}</span>
                <span className="sitasi-stat__label">Menunggu</span>
              </div>
              <div className="sitasi-stat sitasi-stat--approved">
                <span className="sitasi-stat__number">{sitasiStats.approved}</span>
                <span className="sitasi-stat__label">Disetujui</span>
              </div>
              <div className="sitasi-stat sitasi-stat--rejected">
                <span className="sitasi-stat__number">{sitasiStats.rejected}</span>
                <span className="sitasi-stat__label">Ditolak</span>
              </div>
            </div>

            <div className="buku-saya__modal-body sitasi-body">
              {sitasiLoading ? (
                <div className="sitasi-loading">
                  <div className="loading-spinner"></div>
                  <p>Memuat data request sitasi...</p>
                </div>
              ) : sitasiRequests.length === 0 ? (
                <div className="sitasi-empty">
                  <FiFileText />
                  <p>Tidak ada request sitasi</p>
                </div>
              ) : (
                <div className="sitasi-list">
                  {sitasiRequests.map(request => (
                    <div key={request.id_request} className={`sitasi-item sitasi-item--${request.status}`}>
                      <div className="sitasi-item__header">
                        <div className="sitasi-item__book">
                          <FiBook />
                          <span>{request.judul_buku}</span>
                        </div>
                        <span className={`sitasi-item__status sitasi-item__status--${request.status}`}>
                          {request.status === 'pending' ? 'Menunggu' : 
                           request.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                        </span>
                      </div>
                      
                      <div className="sitasi-item__info">
                        <div className="sitasi-item__row">
                          <FiUser />
                          <span><strong>Mahasiswa:</strong> {request.nama_mahasiswa || 'Unknown'}</span>
                        </div>
                        <div className="sitasi-item__row">
                          <FiFileText />
                          <span><strong>NIM:</strong> {request.nim || '-'}</span>
                        </div>
                        <div className="sitasi-item__row">
                          <FiMail />
                          <span><strong>Email:</strong> {request.email_mahasiswa || '-'}</span>
                        </div>
                        <div className="sitasi-item__row">
                          <FiFileText />
                          <span><strong>Keperluan:</strong> {request.keperluan}</span>
                        </div>
                        <div className="sitasi-item__row">
                          <FiCalendar />
                          <span><strong>Tanggal:</strong> {new Date(request.tanggal_request).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                        </div>
                        {request.catatan && (
                          <div className="sitasi-item__row">
                            <FiMessageSquare />
                            <span><strong>Catatan:</strong> {request.catatan}</span>
                          </div>
                        )}
                      </div>
                      
                      {request.status === 'pending' && (
                        <div className="sitasi-item__actions-wrapper">
                          {expandedRequest === request.id_request ? (
                            <div className="sitasi-action-form">
                              <div className="sitasi-action-form__input">
                                <label>
                                  <FiMessageSquare /> Catatan untuk Mahasiswa (opsional):
                                </label>
                                <textarea 
                                  placeholder="Tuliskan catatan atau alasan..."
                                  value={sitasiNotes[request.id_request] || ''}
                                  onChange={(e) => setSitasiNotes(prev => ({
                                    ...prev,
                                    [request.id_request]: e.target.value
                                  }))}
                                  rows={3}
                                />
                              </div>
                              <div className="sitasi-action-form__buttons">
                                <button 
                                  className="sitasi-btn sitasi-btn--cancel"
                                  onClick={() => {
                                    setExpandedRequest(null);
                                    setSitasiNotes(prev => ({ ...prev, [request.id_request]: '' }));
                                  }}
                                >
                                  Batal
                                </button>
                                <button 
                                  className="sitasi-btn sitasi-btn--approve"
                                  onClick={() => handleSitasiAction(request.id_request, 'approved', sitasiNotes[request.id_request] || '')}
                                  disabled={processingRequest === request.id_request}
                                >
                                  <FiCheck /> Konfirmasi Setujui
                                </button>
                                <button 
                                  className="sitasi-btn sitasi-btn--reject"
                                  onClick={() => handleSitasiAction(request.id_request, 'rejected', sitasiNotes[request.id_request] || '')}
                                  disabled={processingRequest === request.id_request}
                                >
                                  <FiX /> Konfirmasi Tolak
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="sitasi-item__actions">
                              <button 
                                className="sitasi-btn sitasi-btn--approve"
                                onClick={() => setExpandedRequest(request.id_request)}
                              >
                                <FiCheck /> Setujui
                              </button>
                              <button 
                                className="sitasi-btn sitasi-btn--reject"
                                onClick={() => setExpandedRequest(request.id_request)}
                              >
                                <FiX /> Tolak
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {request.status !== 'pending' && request.tanggal_respon && (
                        <div className="sitasi-item__response">
                          <small>
                            Direspon pada: {new Date(request.tanggal_respon).toLocaleDateString('id-ID', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </small>
                          {request.catatan_dosen && (
                            <div className="response-notes">
                              <strong>Catatan dari Dosen:</strong>
                              <p>{request.catatan_dosen}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="buku-saya__modal-footer">
              <button 
                className="buku-saya__btn buku-saya__btn--secondary"
                onClick={() => setShowSitasiModal(false)}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BukuSaya;
